/**
 * StyledCard Component
 *
 * Reusable card wrapper with consistent elevation, border radius, and hover effects.
 */
import React from "react";
import { Card, CardProps } from "@mui/material";
import { transparentColors } from "../../utils/theme";

interface StyledCardProps extends CardProps {
  /** Enable hover animation */
  hoverable?: boolean;
  /** Border radius (1-4 scale, where 1=4px, 2=8px, 3=12px) */
  borderRadius?: number;
  /** Show border */
  showBorder?: boolean;
  /** Custom border color */
  borderColor?: string;
  /** Make card interactive (adds focus indicator) */
  interactive?: boolean;
}

/**
 * StyledCard - Enhanced card with consistent styling and hover effects
 *
 * @example
 * ```tsx
 * <StyledCard hoverable borderRadius={3}>
 *   <CardContent>Content</CardContent>
 * </StyledCard>
 * ```
 */
export const StyledCard: React.FC<StyledCardProps> = ({
  hoverable = true,
  borderRadius = 3,
  showBorder = true,
  borderColor = transparentColors.black08,
  elevation = 2,
  interactive = false,
  sx,
  children,
  ...props
}) => (
  <Card
    elevation={elevation}
    sx={{
      borderRadius,
      border: showBorder ? "1px solid" : "none",
      borderColor,
      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
      ...(hoverable && {
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: 6,
        },
      }),
      ...(interactive && {
        "&:focus-visible": {
          outline: "3px solid",
          outlineColor: "primary.main",
          outlineOffset: "2px",
        },
      }),
      ...sx,
    }}
    {...props}
  >
    {children}
  </Card>
);

export default StyledCard;
